import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Bundle, Patient } from 'fhir/r4';
import { UserApproval, PatientRegistrationData } from './patient-registration-center.resolver';

@Injectable()
export class PatientRegistrationCenterStore {
    // Three separate observables for each tab
    pending$ = new BehaviorSubject<UserApproval[]>([]);
    approved$ = new BehaviorSubject<Patient[]>([]);
    deceased$ = new BehaviorSubject<Patient[]>([]);

    // Legacy observable for backward compatibility
    patients$ = new BehaviorSubject<Patient[]>([]);

    /**
     * Initialize store from resolver data
     */
    setFromResolverData(data: PatientRegistrationData) {
        this.pending$.next(data.pending || []);
        this.approved$.next(data.approved || []);
        this.deceased$.next(data.deceased || []);

        // Update legacy patients$ with combined approved and deceased
        const allPatients = [...(data.approved || []), ...(data.deceased || [])];
        this.patients$.next(allPatients);
    }

    /**
     * Legacy method: Initialize from FHIR Bundle (backward compatibility)
     */
    setFromBundle(bundle: Bundle | null | undefined) {
        const entries = bundle?.entry ?? [];
        const patients = entries.map(e => e.resource as Patient);

        // Split patients into approved and deceased
        const approved = patients.filter(p =>
            p.active === true &&
            !(p as any).deceasedBoolean &&
            !(p as any).deceasedDateTime
        );

        const deceased = patients.filter(p =>
            (p as any).deceasedBoolean === true ||
            !!(p as any).deceasedDateTime
        );

        this.approved$.next(approved);
        this.deceased$.next(deceased);
        this.patients$.next(patients);
    }

    /**
     * Remove a pending approval (after approve/reject)
     */
    removePendingApproval(id: string) {
        const current = this.pending$.value.filter(p => p.id !== id);
        this.pending$.next(current);
    }

    /**
     * Add a newly approved patient to the approved list
     */
    addApprovedPatient(patient: Patient) {
        const current = this.approved$.value;
        this.approved$.next([...current, patient]);

        // Update legacy patients$ as well
        this.updateLegacyPatients();
    }

    /**
     * Update an approved patient
     */
    updateApprovedPatient(updated: Patient) {
        const current = this.approved$.value.slice();
        const idx = current.findIndex(p => p.id === updated.id);
        if (idx >= 0) {
            current[idx] = { ...current[idx], ...updated };
            this.approved$.next(current);
            this.updateLegacyPatients();
        }
    }

    /**
     * Remove a patient from approved list
     */
    removeApprovedPatient(id: string) {
        const current = this.approved$.value.filter(p => p.id !== id);
        this.approved$.next(current);
        this.updateLegacyPatients();
    }

    /**
     * Add a patient to deceased list
     */
    addDeceasedPatient(patient: Patient) {
        const current = this.deceased$.value;
        this.deceased$.next([...current, patient]);
        this.updateLegacyPatients();
    }

    /**
     * Update a deceased patient
     */
    updateDeceasedPatient(updated: Patient) {
        const current = this.deceased$.value.slice();
        const idx = current.findIndex(p => p.id === updated.id);
        if (idx >= 0) {
            current[idx] = { ...current[idx], ...updated };
            this.deceased$.next(current);
            this.updateLegacyPatients();
        }
    }

    /**
     * Legacy method: Update a patient (backward compatibility)
     */
    updatePatient(updated: Patient) {
        // Try to update in approved list
        const approvedIdx = this.approved$.value.findIndex(p => p.id === updated.id);
        if (approvedIdx >= 0) {
            this.updateApprovedPatient(updated);
            return;
        }

        // Try to update in deceased list
        const deceasedIdx = this.deceased$.value.findIndex(p => p.id === updated.id);
        if (deceasedIdx >= 0) {
            this.updateDeceasedPatient(updated);
            return;
        }

        // Fallback: update legacy patients$ only
        const current = this.patients$.value.slice();
        const idx = current.findIndex(p => p.id === updated.id);
        if (idx >= 0) {
            current[idx] = { ...current[idx], ...updated };
            this.patients$.next(current);
        }
    }

    /**
     * Legacy method: Remove a patient (backward compatibility)
     */
    removePatient(id: string) {
        this.removeApprovedPatient(id);

        const deceasedCurrent = this.deceased$.value.filter(p => p.id !== id);
        this.deceased$.next(deceasedCurrent);

        this.updateLegacyPatients();
    }

    /**
     * Helper: Sync legacy patients$ observable with approved + deceased
     */
    private updateLegacyPatients() {
        const allPatients = [...this.approved$.value, ...this.deceased$.value];
        this.patients$.next(allPatients);
    }
}
